/* prettier-ignore */

import { inject, Injectable } from '@angular/core';
import { DebugService } from 'src/app/services/debug.service';
import { DataChatService } from './dataChat.service';

/**
 * HRS-3045 — Medición real en navegador del camino de texto del chatbot.
 *
 * Marca con performance.mark/measure las fases del pipeline pregunta→respuesta,
 * correlacionadas por message_id (nombres `chat:{id}:{fase}`):
 *
 *   ask_start            antes del POST /api/v3/ask (aún sin id; se materializa al llegar)
 *   queued               al recibir message_id
 *   stream_open          al abrirse el EventSource (onopen)
 *   first_token_received primer frame token/action_tag
 *   final_received       frame final
 *   done_received        frame done (o cierre equivalente: sse-error/poll/cancel/fail)
 *   render_done          texto definitivo visible tras el paint (doble rAF)
 *
 * Reporta SOLO duraciones, message_id, superficie (web/embed), viewport, capacidades
 * activas, vía de llegada (sse-tokens/final-only/poll) y causa de cierre. Nunca texto,
 * URLs, historial ni contenido de acciones. Salida por DebugService (no-prod/?showDebug).
 * Las marcas/measures se limpian por mensaje al reportar (sesiones largas sin fugas).
 */

export type ChatPerfVia = 'sse-tokens' | 'final-only' | 'poll';
export type ChatPerfPhase =
  | 'queued'
  | 'stream_open'
  | 'first_token_received'
  | 'final_received'
  | 'done_received'
  | 'render_done';

const PHASE_ORDER: ('ask_start' | ChatPerfPhase)[] = [
  'ask_start',
  'queued',
  'stream_open',
  'first_token_received',
  'final_received',
  'done_received',
  'render_done',
];

interface ChatTurnPerf {
  askStart: number;
  phases: Map<ChatPerfPhase, number>;
  via?: ChatPerfVia;
  closeCause?: string;
  reported: boolean;
}

@Injectable({ providedIn: 'root' })
export class ChatPerfService {
  private debug = inject(DebugService);
  private dataChat = inject(DataChatService);

  private turns = new Map<string, ChatTurnPerf>();
  /** timestamp de ask_start del turno en vuelo, antes de conocer su message_id */
  private pendingAskStart?: number;

  /** Antes del POST /api/v3/ask: aún no hay message_id. */
  beginTurn(): void {
    // El turno anterior sin cerrar (respuesta vacía, id nunca resuelto…) se reporta
    // con lo que tenga para no perderlo ni acumular estado.
    for (const id of this.turns.keys())
      this.report(id);

    this.pendingAskStart = performance.now();
  }

  /** Al recibir message_id: materializa ask_start (retroactivo) y marca queued. */
  attachId(messageId: string): void {
    if (this.pendingAskStart == null || !messageId)
      return;

    const turn: ChatTurnPerf = {
      askStart: this.pendingAskStart,
      phases: new Map(),
      reported: false,
    };
    this.pendingAskStart = undefined;
    this.turns.set(messageId, turn);

    this.tryMark(`chat:${messageId}:ask_start`, turn.askStart);
    this.mark(messageId, 'queued');
  }

  /** Marca una fase (solo la primera vez que llega). */
  mark(messageId: string, phase: ChatPerfPhase): void {
    const turn = this.turns.get(messageId);
    if (!turn || turn.phases.has(phase))
      return;

    turn.phases.set(phase, performance.now());
    this.tryMark(`chat:${messageId}:${phase}`);
  }

  /** Vía de llegada del texto; solo se fija una vez. */
  setVia(messageId: string, via: ChatPerfVia): void {
    const turn = this.turns.get(messageId);
    if (turn && !turn.via)
      turn.via = via;
  }

  /**
   * Cierre del stream (done/sse-error/poll/cancel/fail). Con reportNow (cancel/fail)
   * se reporta ya, sin esperar render_done.
   */
  close(messageId: string, cause: string, reportNow = false): void {
    const turn = this.turns.get(messageId);
    if (!turn)
      return;

    if (!turn.closeCause) {
      turn.closeCause = cause;
      this.mark(messageId, 'done_received');
    }

    if (reportNow)
      this.report(messageId);
    else
      this.maybeReport(messageId);
  }

  /**
   * El render del mensaje quedó completado (messageRendered$ del servicio de
   * animación): render_done se marca tras el paint (doble rAF).
   */
  onMessageRendered(messageId: string): void {
    const turn = this.turns.get(messageId);
    if (!turn || turn.phases.has('render_done'))
      return;

    this.afterPaint(() => {
      this.mark(messageId, 'render_done');
      this.maybeReport(messageId);
    });
  }

  /** Reporta cuando el turno tiene cierre Y render_done (llegan en cualquier orden). */
  private maybeReport(messageId: string): void {
    const turn = this.turns.get(messageId);
    if (turn && turn.closeCause && turn.phases.has('render_done'))
      this.report(messageId);
  }

  private report(messageId: string): void {
    const turn = this.turns.get(messageId);
    if (!turn || turn.reported)
      return;
    turn.reported = true;

    // Duraciones acumuladas desde ask_start + measures entre fases consecutivas presentes
    const durations: Record<string, number> = {};
    let prevName: string = 'ask_start';
    let prevTime = turn.askStart;
    for (const phase of PHASE_ORDER) {
      if (phase === 'ask_start') continue;
      const t = turn.phases.get(phase);
      if (t == null) continue;
      durations[`${phase}_ms`] = Math.round(t - turn.askStart);
      this.tryMeasure(`chat:${messageId}:${prevName}->${phase}`, prevTime, t);
      prevName = phase;
      prevTime = t;
    }

    const finalT = turn.phases.get('final_received');
    const renderT = turn.phases.get('render_done');
    const finalToRender = finalT != null && renderT != null ? Math.round(renderT - finalT) : undefined;

    this.debug.info(
      {
        message_id: messageId,
        via: turn.via ?? 'final-only',
        close_cause: turn.closeCause ?? 'unresolved',
        surface: this.surface(),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        capabilities: {
          voice: this.dataChat.allowSendAudio(),
          image: this.dataChat.allowSendImage(),
          proactive: this.dataChat.allowProactiveBubbles(),
        },
        durations_ms: durations,
        // criterio de aceptación HRS-3045: <= 300 ms
        final_to_render_ms: finalToRender,
      },
      '[ChatPerf]',
    );

    this.cleanup(messageId);
  }

  private cleanup(messageId: string): void {
    try {
      performance.clearMarks(`chat:${messageId}:ask_start`);
      for (const phase of PHASE_ORDER)
        performance.clearMarks(`chat:${messageId}:${phase}`);
      performance
        .getEntriesByType('measure')
        .filter((e) => e.name.startsWith(`chat:${messageId}:`))
        .forEach((e) => performance.clearMeasures(e.name));
    } catch { /* medición best-effort */ }
    this.turns.delete(messageId);
  }

  private surface(): 'web' | 'embed' {
    return window.location.pathname.includes('chatbot-embed') ? 'embed' : 'web';
  }

  /** Doble rAF = después del paint; con la tab oculta el rAF no dispara → timer. */
  private afterPaint(cb: () => void): void {
    if (typeof requestAnimationFrame !== 'function' || document.hidden) {
      setTimeout(cb, 0);
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(cb));
  }

  private tryMark(name: string, startTime?: number): void {
    try {
      if (startTime != null)
        performance.mark(name, { startTime });
      else
        performance.mark(name);
    } catch { /* mark con opciones no soportado: la medición no debe romper el chat */ }
  }

  private tryMeasure(name: string, start: number, end: number): void {
    try {
      performance.measure(name, { start, end });
    } catch { /* measure con opciones no soportado */ }
  }
}
